/**
 * DocAIPanel — KI-Dokumentenanalyse Panel
 * 
 * Zeigt KI-Analyse-Ergebnisse für E-Mail-Anhänge:
 * - Stellungnahme-Alert (rot, prominent)
 * - Email-Kategorie Badge
 * - Aktenverknüpfung (AZ, Kennzeichen)
 * - Dokumenttyp-Erkennung
 * - Ablagevorschlag
 * - Empfohlene Aktionen (Stellungnahme, Überweisung, Aufgabe, etc.)
 * - Metadaten (Betrag, IBAN, Versicherung, Anwalt, etc.)
 */
import { useState } from 'react';
import {
  Brain, FolderOpen, CreditCard, ListTodo, Clock, FileCheck,
  ChevronDown, ChevronUp, Sparkles, AlertTriangle, CheckCircle2,
  ArrowRight, Loader2, Building2, Receipt, FileText, X,
  Zap, Target, BookOpen, Send, Scale, FolderInput, Reply,
  Link2, Car, Shield, FileWarning,
} from 'lucide-react';
import { toast } from 'sonner';
import type { DocAIAnalyse, DocAIAktion } from '@/hooks/useDocAI';
import { getAktionLabel, getPrioritaetColor, getDokumentTypIcon, getEmailKategorieLabel, getEmailKategorieColor } from '@/hooks/useDocAI';

interface DocAIPanelProps {
  analysis: DocAIAnalyse | null;
  analyzing: boolean;
  onAnalyze: () => void;
  onCreateTask?: (aktion: DocAIAktion) => void;
  onFileToFolder?: (pfad: string) => void;
  onCreateTransfer?: (aktion: DocAIAktion) => void;
  onDismiss?: () => void;
  onOpenAkte?: (aktenzeichen: string) => void;
  onCreateStellungnahme?: (analysis: DocAIAnalyse) => void;
}

export default function DocAIPanel({
  analysis,
  analyzing,
  onAnalyze,
  onCreateTask,
  onFileToFolder,
  onCreateTransfer,
  onDismiss,
  onOpenAkte,
  onCreateStellungnahme,
}: DocAIPanelProps) {
  const [expanded, setExpanded] = useState(true);
  const [showMetadaten, setShowMetadaten] = useState(false);

  // Nicht analysiert: Analyse-Button anzeigen
  if (!analysis && !analyzing) {
    return (
      <button
        onClick={onAnalyze}
        className="w-full flex items-center gap-2 px-3 py-2.5 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg hover:from-purple-100 hover:to-blue-100 transition-all text-sm"
      >
        <Brain className="w-4 h-4 text-purple-600" />
        <span className="font-medium text-purple-700">KI-Analyse starten</span>
        <Sparkles className="w-3.5 h-3.5 text-purple-400 ml-auto" />
      </button>
    );
  }

  // Analysiert gerade
  if (analyzing) {
    return (
      <div className="flex items-center gap-2 px-3 py-2.5 bg-purple-50 border border-purple-200 rounded-lg text-sm">
        <Loader2 className="w-4 h-4 text-purple-600 animate-spin" />
        <span className="text-purple-700 font-medium">KI analysiert Dokument...</span>
      </div>
    );
  }

  if (!analysis) return null;

  const konfidenzColor = analysis.konfidenz >= 70 ? 'text-green-600' : analysis.konfidenz >= 40 ? 'text-yellow-600' : 'text-red-600';
  const konfidenzBg = analysis.konfidenz >= 70 ? 'bg-green-100' : analysis.konfidenz >= 40 ? 'bg-yellow-100' : 'bg-red-100';
  const kategorieColor = analysis.emailKategorie ? getEmailKategorieColor(analysis.emailKategorie) : '#6B7280';

  return (
    <div className={`border rounded-lg overflow-hidden ${
      analysis.stellungnahmeErforderlich 
        ? 'border-red-300 bg-gradient-to-b from-red-50/80 to-white' 
        : 'border-purple-200 bg-gradient-to-b from-purple-50/50 to-white'
    }`}>
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-purple-50/50 transition-colors"
      >
        <Brain className="w-4 h-4 text-purple-600 flex-shrink-0" />
        <span className="text-sm font-bold text-purple-800 flex-1 text-left">KI-Analyse</span>
        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${konfidenzBg} ${konfidenzColor}`}>
          {analysis.konfidenz}%
        </span>
        {onDismiss && (
          <button
            onClick={(e) => { e.stopPropagation(); onDismiss(); }}
            className="p-0.5 hover:bg-purple-200 rounded"
          >
            <X className="w-3 h-3 text-purple-400" />
          </button>
        )}
        {expanded ? <ChevronUp className="w-4 h-4 text-purple-400" /> : <ChevronDown className="w-4 h-4 text-purple-400" />}
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-2.5">
          {/* STELLUNGNAHME ALERT — Prominent! */}
          {analysis.stellungnahmeErforderlich && (
            <div className="bg-red-50 border-2 border-red-300 rounded-lg p-2.5 animate-pulse-once">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <FileWarning className="w-4.5 h-4.5 text-red-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-red-800">Stellungnahme erforderlich!</p>
                  <p className="text-[10px] text-red-600 mt-0.5">
                    {analysis.dokumentTypLabel}
                    {analysis.aktenVerknuepfung?.aktenzeichen && ` — Akte ${analysis.aktenVerknuepfung.aktenzeichen}`}
                  </p>
                </div>
                {onCreateStellungnahme && (
                  <button
                    onClick={() => onCreateStellungnahme(analysis)}
                    className="px-2.5 py-1.5 bg-red-600 hover:bg-red-700 text-white text-[10px] font-bold rounded-lg transition-colors flex items-center gap-1 flex-shrink-0"
                  >
                    <Scale className="w-3 h-3" />
                    Erstellen
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Zusammenfassung */}
          <p className="text-xs text-gray-600 leading-relaxed">{analysis.zusammenfassung}</p>

          {/* Email-Kategorie + Dokumenttyp + Firma */}
          <div className="flex items-center gap-2">
            <span className="text-base">{getDokumentTypIcon(analysis.dokumentTyp)}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="text-xs font-bold text-gray-800">{analysis.dokumentTypLabel}</p>
                {analysis.emailKategorie && (
                  <span 
                    className="text-[9px] font-bold px-1.5 py-0.5 rounded-full text-white"
                    style={{ backgroundColor: kategorieColor }}
                  >
                    {getEmailKategorieLabel(analysis.emailKategorie)}
                  </span>
                )}
              </div>
              <p className="text-[10px] text-gray-500 flex items-center gap-1">
                <Building2 className="w-3 h-3" />
                {analysis.firmaName}
              </p>
            </div>
          </div>

          {/* Aktenverknüpfung */}
          {analysis.aktenVerknuepfung && (analysis.aktenVerknuepfung.aktenzeichen || analysis.aktenVerknuepfung.kennzeichen || analysis.aktenVerknuepfung.versicherung) && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
              <div className="flex items-center gap-1.5 mb-1">
                <Link2 className="w-3.5 h-3.5 text-amber-600" />
                <span className="text-[10px] font-bold text-amber-700 uppercase tracking-wide">Aktenverknüpfung</span>
              </div>
              <div className="space-y-0.5">
                {analysis.aktenVerknuepfung.aktenzeichen && (
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-amber-700">Aktenzeichen</span>
                    <button
                      onClick={() => onOpenAkte?.(analysis.aktenVerknuepfung.aktenzeichen!)}
                      className="text-xs font-bold text-amber-800 hover:text-amber-900 hover:underline flex items-center gap-1"
                    >
                      {analysis.aktenVerknuepfung.aktenzeichen}
                      <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                )}
                {analysis.aktenVerknuepfung.kennzeichen && (
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-amber-700 flex items-center gap-1"><Car className="w-3 h-3" /> Kennzeichen</span>
                    <span className="text-xs font-bold text-amber-800 font-mono">{analysis.aktenVerknuepfung.kennzeichen}</span>
                  </div>
                )}
                {analysis.aktenVerknuepfung.schadennummer && (
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-amber-700">Schadennr.</span>
                    <span className="text-xs font-medium text-amber-800 font-mono">{analysis.aktenVerknuepfung.schadennummer}</span>
                  </div>
                )}
                {analysis.aktenVerknuepfung.versicherung && (
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-amber-700 flex items-center gap-1"><Shield className="w-3 h-3" /> Versicherung</span>
                    <span className="text-xs font-medium text-amber-800">{analysis.aktenVerknuepfung.versicherung}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Ablagevorschlag */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
            <div className="flex items-center gap-1.5 mb-1">
              <FolderOpen className="w-3.5 h-3.5 text-blue-600" />
              <span className="text-[10px] font-bold text-blue-700 uppercase tracking-wide">Ablagevorschlag</span>
            </div>
            <p className="text-xs text-blue-800 font-medium">{analysis.ablageVorschlag.pfad}</p>
            {onFileToFolder && (
              <button
                onClick={() => onFileToFolder(analysis.ablageVorschlag.pfad)}
                className="mt-1.5 flex items-center gap-1 text-[10px] font-bold text-blue-600 hover:text-blue-800 transition-colors"
              >
                <ArrowRight className="w-3 h-3" />
                Anhang hier ablegen
              </button>
            )}
          </div>

          {/* Aktionen */}
          {analysis.aktionen.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                <Zap className="w-3 h-3" />
                Empfohlene Aktionen
              </p>
              {analysis.aktionen.map((aktion, i) => (
                <div
                  key={i}
                  className={`bg-white border rounded-lg p-2 hover:border-gray-300 transition-colors ${
                    aktion.typ === 'stellungnahme' ? 'border-red-200 bg-red-50/30' :
                    aktion.typ === 'antwort_anwalt' ? 'border-purple-200 bg-purple-50/30' :
                    aktion.typ === 'akte_ablage' ? 'border-amber-200 bg-amber-50/30' :
                    'border-gray-200'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ backgroundColor: getPrioritaetColor(aktion.prioritaet) + '20', color: getPrioritaetColor(aktion.prioritaet) }}
                    >
                      {aktion.typ === 'überweisung' ? <CreditCard className="w-3 h-3" /> :
                       aktion.typ === 'aufgabe' ? <ListTodo className="w-3 h-3" /> :
                       aktion.typ === 'frist' ? <Clock className="w-3 h-3" /> :
                       aktion.typ === 'prüfung' ? <FileCheck className="w-3 h-3" /> :
                       aktion.typ === 'stellungnahme' ? <Scale className="w-3 h-3" /> :
                       aktion.typ === 'akte_ablage' ? <FolderInput className="w-3 h-3" /> :
                       aktion.typ === 'antwort_anwalt' ? <Reply className="w-3 h-3" /> :
                       <Target className="w-3 h-3" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-gray-800">{aktion.titel}</p>
                      <p className="text-[10px] text-gray-500 mt-0.5">{aktion.beschreibung}</p>
                      {aktion.frist && (
                        <p className="text-[10px] text-red-600 font-medium mt-0.5 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Frist: {new Date(aktion.frist).toLocaleDateString('de-DE')}
                        </p>
                      )}
                      {aktion.betrag && (
                        <p className="text-xs font-bold text-gray-900 mt-1">
                          {aktion.betrag.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                        </p>
                      )}
                      {aktion.iban && (
                        <p className="text-[10px] text-gray-500 font-mono mt-0.5">IBAN: {aktion.iban}</p>
                      )}
                      {aktion.verwendungszweck && (
                        <p className="text-[10px] text-gray-500 mt-0.5">VWZ: {aktion.verwendungszweck}</p>
                      )}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      {aktion.typ === 'stellungnahme' && onCreateStellungnahme && (
                        <button
                          onClick={() => { onCreateStellungnahme(analysis); toast.success('Stellungnahme wird vorbereitet...'); }}
                          className="p-1 bg-red-100 hover:bg-red-200 rounded text-red-700 transition-colors"
                          title="Stellungnahme erstellen"
                        >
                          <Scale className="w-3 h-3" />
                        </button>
                      )}
                      {aktion.typ === 'akte_ablage' && onFileToFolder && (
                        <button
                          onClick={() => { onFileToFolder(aktion.beschreibung); toast.success('In Akte abgelegt'); }}
                          className="p-1 bg-amber-100 hover:bg-amber-200 rounded text-amber-700 transition-colors"
                          title="In Akte ablegen"
                        >
                          <FolderInput className="w-3 h-3" />
                        </button>
                      )}
                      {aktion.typ === 'antwort_anwalt' && (
                        <button
                          onClick={() => { toast.success('Antwort-Entwurf wird erstellt...'); }}
                          className="p-1 bg-purple-100 hover:bg-purple-200 rounded text-purple-700 transition-colors"
                          title="Anwalt antworten"
                        >
                          <Reply className="w-3 h-3" />
                        </button>
                      )}
                      {aktion.typ === 'überweisung' && onCreateTransfer && (
                        <button
                          onClick={() => { onCreateTransfer(aktion); toast.success('Überweisung erstellt'); }}
                          className="p-1 bg-green-100 hover:bg-green-200 rounded text-green-700 transition-colors"
                          title="Überweisung erstellen"
                        >
                          <Send className="w-3 h-3" />
                        </button>
                      )}
                      {(aktion.typ === 'aufgabe' || aktion.typ === 'prüfung' || aktion.typ === 'frist') && onCreateTask && (
                        <button
                          onClick={() => { onCreateTask(aktion); toast.success('Aufgabe erstellt'); }}
                          className="p-1 bg-blue-100 hover:bg-blue-200 rounded text-blue-700 transition-colors"
                          title="Aufgabe erstellen"
                        >
                          <ListTodo className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Metadaten (aufklappbar) */}
          {analysis.metadaten && Object.keys(analysis.metadaten).filter(k => (analysis.metadaten as any)[k]).length > 0 && (
            <div>
              <button
                onClick={() => setShowMetadaten(!showMetadaten)}
                className="flex items-center gap-1 text-[10px] font-bold text-gray-400 hover:text-gray-600 transition-colors uppercase tracking-wide"
              >
                <BookOpen className="w-3 h-3" />
                Extrahierte Daten
                {showMetadaten ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
              {showMetadaten && (
                <div className="mt-1.5 bg-gray-50 rounded-lg p-2 space-y-1">
                  {Object.entries(analysis.metadaten).filter(([_, v]) => v).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between text-[10px]">
                      <span className="text-gray-500 capitalize">{
                        key === 'aktenzeichen' ? 'Aktenzeichen' :
                        key === 'kennzeichen' ? 'Kennzeichen' :
                        key === 'schadennummer' ? 'Schadennr.' :
                        key === 'versicherung' ? 'Versicherung' :
                        key === 'pruefdienstleister' ? 'Prüfdienstleister' :
                        key === 'anwalt' ? 'Anwalt/Kanzlei' :
                        key === 'klaeger' ? 'Kläger' :
                        key === 'beklagter' ? 'Beklagter' :
                        key.replace(/_/g, ' ')
                      }</span>
                      <span className="text-gray-800 font-medium font-mono">
                        {typeof value === 'number' 
                          ? value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })
                          : String(value)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
